import Button from 'components/Form/Button';
import { Check } from 'components/Form/Check';
import Input, { CurrencyInput } from 'components/Form/Input';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { SettlementReversal } from 'components/Modal/HistoricoCobranca/components/Estorno';
import { baixaParcela } from 'services/cobranca';
import { ReadByCobranca } from 'services/titulo';
import { EmptyMessage } from 'ui/styled';
import { exposeStrapiError, normalizeResponse } from 'utils';
import { installmentLabel } from 'utils/billing';
import { parseCurrency, parseDecimal } from 'utils/parsers';
import {
  ActionBar,
  ActionButtons,
  ActionTotal,
  Cell,
  CellLink,
  CellNote,
  InstallmentHeader,
  InstallmentList,
  InstallmentRow,
  ListFallback,
  ModeBar,
  SettlementContainer,
  SettlementHint,
  WarningBox,
  WarningText,
  WarningTitle,
} from './styled';

// Quem ve a aba. Comparado com `true` estrito: o administrador nao dispensa a
// flag, o backend devolve 403 para admin sem ela.
export const canSettleManually = (user) => user?.baixa_manual === true;

const toNumber = (value) => {
  const number = Number(value);
  return value === null || value === undefined || value === '' || isNaN(number)
    ? null
    : number;
};

// Minimo aceito para o valor recebido: o valor da parcela menos o desconto
// maximo da distribuidora, sem mora nem multa. Sem limite cadastrado, nenhum
// desconto e aceito. E so referencia para o operador: quem valida e o backend.
const minimumFor = (value, limit) => {
  const percent = toNumber(limit);
  if (!percent || percent <= 0) {
    return value;
  }
  return Math.round(value * (1 - percent / 100) * 100) / 100;
};

const round2 = (value) => Math.round(value * 100) / 100;

// Percentual de desconto como o operador le: virgula decimal e sem zeros a
// toa ("10", "10,5"). Sem desconto (valor integral ou acima da parcela), vazio.
const formatPercent = (percent) =>
  percent > 0 ? `${round2(percent)}`.replace('.', ',') : '';

const plural = (n, singular, pluralForm) => (n === 1 ? singular : pluralForm);

// Data da baixa no formato da API. O boleto compensa dias depois do
// pagamento, entao o operador informa a data real; o padrao e hoje.
const todayIso = () => moment().format('YYYY-MM-DD');

const formatDate = (iso) => moment(iso, 'YYYY-MM-DD').format('DD/MM/YYYY');

// Por que a data nao serve, ou `null`. A data anterior a emissao do titulo so
// o backend confere: a emissao nao vem em todos os titulos.
const dateError = (iso) => {
  const date = moment(iso, 'YYYY-MM-DD', true);
  if (!date.isValid()) return 'Informe a data da baixa';
  if (date.isAfter(moment(), 'day')) return 'Data da baixa no futuro';
  return null;
};

// O 403 desta rota e falta da flag `baixa_manual`, e nao sessao expirada.
const handleError = (result) => {
  if (result && result?.error?.status === 403) {
    toast.error('Você não tem permissão para realizar baixa manual.');
    return true;
  }
  if (exposeStrapiError(result)) return true;
  if (!Array.isArray(result?.titulos_baixados)) {
    toast.error('A baixa não devolveu os títulos baixados.');
    return true;
  }
  return false;
};

// Aba Baixa do modal de Historico: baixa manual de um ou mais titulos em
// aberto do contrato. Lista todos os titulos nao baixados (`row.titulos` vem
// inteiro, nao so as parcelas visiveis na tela de origem) e pede confirmacao
// antes de gravar.
//
// `onSettled` roda depois de uma baixa parcial (o modal continua aberto, com as
// baixadas fora da lista); `onClosed`, quando a cobranca foi encerrada e sai
// das telas da esteira. `onTotal` informa quantos titulos seguem em aberto,
// para o badge da aba.
export const ManualSettlement = ({ row, onSettled, onClosed, onTotal }) => {
  const discountLimit = row?.discountLimit;
  const [settledParcels, setSettledParcels] = useState([]);
  // Aberto pela linha de uma parcela, ela ja vem marcada.
  const [selected, setSelected] = useState(() => {
    const origin = (row?.titulos || []).find(
      (titulo) => titulo?.id && titulo.id === row?.tituloId
    );
    return origin && origin.status !== 'baixado' ? [origin.parcela] : [];
  });
  // Valor recebido, % de desconto e desconto em reais sao o mesmo dado em tres
  // formas: o operador preenche qualquer um e os outros sao calculados sobre o
  // valor da parcela. O valor recebido e a fonte; o desconto em reais e
  // derivado dele. O percentual guarda o texto digitado, para a virgula nao
  // sumir no meio da digitacao.
  const [received, setReceived] = useState({});
  const [discountPercent, setDiscountPercent] = useState({});
  // Data da baixa por parcela, em AAAA-MM-DD. Sem entrada aqui, vale hoje.
  const [settleDates, setSettleDates] = useState({});
  // edit -> confirm -> saving
  const [step, setStep] = useState('edit');

  const installments = useMemo(
    () =>
      (row?.titulos || [])
        .filter(
          (titulo) =>
            titulo?.status !== 'baixado' &&
            !settledParcels.includes(titulo?.parcela)
        )
        .map((titulo) => {
          const value = toNumber(titulo?.valor) || 0;
          return {
            // A entrada da renegociacao e baixada como parcela 0, pela mesma
            // rota, mas sempre pelo valor integral (ver `minimum`).
            parcela: titulo?.parcela,
            label: installmentLabel(titulo),
            entry: titulo?.entrada === true,
            dueDate: titulo?.data_vencimento,
            overdue: titulo?.status === 'vencido',
            daysOverdue: titulo?.dias_vencido,
            value,
            // A entrada e baixada sempre pelo valor integral: sem desconto e sem
            // alterar o valor recebido (os tres campos ficam travados).
            minimum:
              titulo?.entrada === true
                ? value
                : minimumFor(value, discountLimit),
          };
        })
        .sort((a, b) => (a.parcela || 0) - (b.parcela || 0)),
    [row?.titulos, settledParcels, discountLimit]
  );

  useEffect(() => {
    onTotal?.(installments.length);
  }, [installments.length, onTotal]);

  // Valor recebido: o que o operador digitou, ou o valor integral da parcela.
  const receivedFor = (item) =>
    received[item.parcela] === undefined ? item.value : received[item.parcela];

  const percentFor = (item) => discountPercent[item.parcela] ?? '';

  const dateFor = (item) => settleDates[item.parcela] ?? todayIso();

  const changeDate = (item, value) =>
    setSettleDates((current) => ({ ...current, [item.parcela]: value }));

  // "Aplicar a todas": a data desta parcela vale para todas as da lista.
  const applyDateToAll = (value) =>
    setSettleDates(
      Object.fromEntries(installments.map((item) => [item.parcela, value]))
    );

  const changePercent = (item, text) => {
    const typed = `${text ?? ''}`.replace(/[^\d,.]/g, '');
    const percent = parseDecimal(typed, null);
    setDiscountPercent((current) => ({ ...current, [item.parcela]: typed }));
    setReceived((current) => ({
      ...current,
      [item.parcela]:
        percent === null
          ? item.value
          : round2(
              item.value * (1 - Math.min(Math.max(percent, 0), 100) / 100)
            ),
    }));
  };

  const changeReceived = (item, value) => {
    // O CurrencyFormat repete o onValueChange no blur com o mesmo valor; sem
    // esta guarda, sair do campo reescreveria o percentual digitado.
    if (value === receivedFor(item)) {
      return;
    }
    setReceived((current) => ({ ...current, [item.parcela]: value }));
    setDiscountPercent((current) => ({
      ...current,
      [item.parcela]:
        value === '' || !item.value
          ? ''
          : formatPercent(((item.value - value) / item.value) * 100),
    }));
  };

  // Desconto em reais: o que falta do recebido para o valor da parcela.
  const discountFor = (item) => {
    const value = toNumber(receivedFor(item));
    const discount = value === null ? 0 : round2(item.value - value);
    return discount > 0 ? discount : '';
  };

  const changeDiscount = (item, value) => {
    // Mesma guarda do changeReceived: o blur repete o valor atual.
    if (value === discountFor(item)) {
      return;
    }
    const discount =
      value === '' ? 0 : Math.min(Math.max(value, 0), item.value);
    setReceived((current) => ({
      ...current,
      [item.parcela]: round2(item.value - discount),
    }));
    setDiscountPercent((current) => ({
      ...current,
      [item.parcela]: item.value
        ? formatPercent((discount / item.value) * 100)
        : '',
    }));
  };

  const chosen = installments.filter((item) => selected.includes(item.parcela));
  const total = chosen.reduce(
    (sum, item) => sum + (toNumber(receivedFor(item)) || 0),
    0
  );
  const saving = step === 'saving';

  const toggle = (parcela) =>
    setSelected((current) =>
      current.includes(parcela)
        ? current.filter((item) => item !== parcela)
        : [...current, parcela]
    );

  const review = () => {
    const missing = chosen.find((item) => !(toNumber(receivedFor(item)) > 0));
    if (missing) {
      toast.error(
        missing.entry
          ? 'Informe o valor recebido da entrada.'
          : `Informe o valor recebido da parcela ${missing.parcela}.`
      );
      return;
    }
    const invalidDate = chosen.find((item) => dateError(dateFor(item)));
    if (invalidDate) {
      toast.error(
        `${dateError(dateFor(invalidDate))} (${
          invalidDate.entry ? 'entrada' : `parcela ${invalidDate.parcela}`
        }).`
      );
      return;
    }
    setStep('confirm');
  };

  const settle = async () => {
    setStep('saving');
    const items = chosen.map((item) => {
      // Entrada: sempre o valor integral, mesmo que algo tenha mudado o estado.
      const value = item.entry ? item.value : toNumber(receivedFor(item));
      const discount = round2(item.value - value);
      // A data vai em todo item, entrada inclusive: o backend a exige e
      // recalcula juros, multa e IOF ate ela.
      const data_baixa = dateFor(item);
      // Sem desconto (integral ou acima da parcela), os dois campos ficam de
      // fora: sao opcionais e o backend calcula sozinho.
      if (!(discount > 0) || !item.value) {
        return { parcela: item.parcela, valor_recebido: value, data_baixa };
      }
      // O percentual sai do valor recebido, com 6 casas, e nao do texto da
      // tela: o backend confere os dois com tolerancia de R$ 0,01, e o
      // percentual exibido (2 casas) erra mais que isso em parcela de algumas
      // centenas de reais (19,21% de R$ 495,14 da R$ 400,02, nao R$ 400,00).
      return {
        parcela: item.parcela,
        valor_recebido: value,
        perc_desconto:
          Math.round(((item.value - value) / item.value) * 100 * 1e6) / 1e6,
        valor_desconto: discount,
        data_baixa,
      };
    });
    const result = await baixaParcela({ data: items }, row?.id);
    if (handleError(result)) {
      setStep('confirm');
      return;
    }

    const settledNow = result.titulos_baixados;
    const discount = settledNow.reduce(
      (sum, item) => sum + (toNumber(item?.desconto) || 0),
      0
    );
    toast.success(
      `${settledNow.length} ${plural(
        settledNow.length,
        'parcela baixada',
        'parcelas baixadas'
      )}${discount > 0 ? `, com ${parseCurrency(discount)} de desconto` : ''}.`
    );

    // Banco sem formula de juros: a mora nao foi recalculada ate a data da
    // baixa, e ficou a que estava gravada.
    const notRecalculated = settledNow.filter(
      (item) => item?.mora_recalculada === false
    );
    if (notRecalculated.length) {
      toast.info(
        `O banco não tem fórmula de juros cadastrada: a mora ${plural(
          notRecalculated.length,
          'da parcela',
          'das parcelas'
        )} ${notRecalculated
          .map((item) => item?.parcela)
          .join(', ')} não foi recalculada até a data da baixa.`
      );
    }

    if (result.encerrada) {
      toast.info(
        'A cobrança foi finalizada: não há mais títulos em aberto, e o contrato sai das telas da esteira.'
      );
      onClosed?.();
      return;
    }

    setSettledParcels((current) => [
      ...current,
      ...settledNow.map((item) => item?.parcela),
    ]);
    setSelected([]);
    setReceived({});
    setDiscountPercent({});
    setSettleDates({});
    setStep('edit');
    onSettled?.();
  };

  if (!installments.length) {
    return (
      <ListFallback>
        <EmptyMessage>Nenhum título em aberto neste contrato.</EmptyMessage>
      </ListFallback>
    );
  }

  return (
    <SettlementContainer>
      <SettlementHint>
        Informe a data em que o pagamento foi feito (o padrão é hoje): juros,
        multa e IOF são recalculados até ela, e pagamento até o vencimento fica
        sem mora. O desconto é calculado só sobre o valor da parcela, sem mora
        nem multa:{' '}
        {toNumber(discountLimit) > 0
          ? `esta distribuidora aceita até ${discountLimit}%.`
          : 'esta distribuidora não tem limite cadastrado, então só o valor integral é aceito.'}{' '}
        Valor acima da parcela é aceito.
      </SettlementHint>

      {step === 'edit' ? (
        <>
          <InstallmentHeader>
            <span />
            <span>Parcela</span>
            <span>Vencimento</span>
            <span>Data da baixa</span>
            <span>Valor</span>
            <span>Mínimo</span>
            <span>% Desconto</span>
            <span>Desconto</span>
            <span>Valor recebido</span>
          </InstallmentHeader>
          <InstallmentList>
            {installments.map((item) => {
              const isSelected = selected.includes(item.parcela);
              const value = toNumber(receivedFor(item));
              const belowMinimum =
                isSelected && value !== null && value < item.minimum;
              return (
                <InstallmentRow key={item.parcela} $selected={isSelected}>
                  <Check
                    checked={isSelected}
                    onChange={() => toggle(item.parcela)}
                  />
                  <Cell>{item.label}</Cell>
                  <Cell>
                    {item.dueDate
                      ? moment(item.dueDate).format('DD/MM/YYYY')
                      : '-'}
                    <CellNote>
                      {item.overdue
                        ? `Vencida${
                            item.daysOverdue
                              ? ` há ${item.daysOverdue} dias`
                              : ''
                          }`
                        : 'A vencer'}
                    </CellNote>
                  </Cell>
                  <Cell $anchor>
                    <Input
                      holder
                      type="date"
                      value={dateFor(item)}
                      disabled={!isSelected}
                      onChange={(e) => changeDate(item, e.target.value)}
                    />
                    {isSelected && dateError(dateFor(item)) ? (
                      <CellNote $warning $floating>
                        {dateError(dateFor(item))}
                      </CellNote>
                    ) : isSelected && installments.length > 1 ? (
                      <CellLink
                        type="button"
                        onClick={() => applyDateToAll(dateFor(item))}
                      >
                        Aplicar a todas
                      </CellLink>
                    ) : null}
                  </Cell>
                  <Cell>{parseCurrency(item.value)}</Cell>
                  <Cell>{parseCurrency(item.minimum)}</Cell>
                  <Cell>
                    <Input
                      holder
                      placeholder="0,00"
                      value={percentFor(item)}
                      disabled={!isSelected || item.entry}
                      onChange={(e) => changePercent(item, e.target.value)}
                    />
                  </Cell>
                  <Cell>
                    <CurrencyInput
                      holder
                      placeholder="R$ 0,00"
                      value={discountFor(item)}
                      disabled={!isSelected || item.entry}
                      onChange={(e) => changeDiscount(item, e.target.value)}
                    />
                  </Cell>
                  <Cell $anchor>
                    <CurrencyInput
                      holder
                      placeholder="R$ 0,00"
                      value={receivedFor(item)}
                      disabled={!isSelected || item.entry}
                      onChange={(e) => changeReceived(item, e.target.value)}
                    />
                    {belowMinimum ? (
                      <CellNote
                        $warning
                        $floating
                        title={`O valor recebido mínimo desta parcela é ${parseCurrency(
                          item.minimum
                        )}`}
                      >
                        {toNumber(discountLimit) > 0
                          ? `Desconto acima do limite de ${formatPercent(
                              toNumber(discountLimit)
                            )}%`
                          : 'Distribuidora não aceita desconto'}
                      </CellNote>
                    ) : null}
                  </Cell>
                </InstallmentRow>
              );
            })}
          </InstallmentList>
        </>
      ) : (
        <WarningBox>
          <WarningTitle>Confirme a baixa</WarningTitle>
          <WarningText>
            {chosen
              .map(
                (item) =>
                  `${
                    item.entry ? 'Entrada' : `Parcela ${item.parcela}`
                  }: ${parseCurrency(
                    toNumber(receivedFor(item))
                  )} em ${formatDate(dateFor(item))}${
                    percentFor(item)
                      ? ` (desconto de ${percentFor(item)}%)`
                      : ''
                  }`
              )
              .join('  |  ')}
          </WarningText>
          <WarningText>
            {chosen.length}{' '}
            {plural(
              chosen.length,
              'parcela será baixada',
              'parcelas serão baixadas'
            )}
            , num total de {parseCurrency(total)}. Juros, multa e IOF são
            recalculados até a data de cada baixa. Uma baixa lançada errado só é
            desfeita por estorno, com justificativa e permissão própria.
          </WarningText>
        </WarningBox>
      )}

      <ActionBar>
        <ActionTotal>
          {chosen.length
            ? `${chosen.length} ${plural(
                chosen.length,
                'parcela selecionada',
                'parcelas selecionadas'
              )} · ${parseCurrency(total)}`
            : 'Selecione as parcelas a baixar'}
        </ActionTotal>
        <ActionButtons>
          {step === 'edit' ? (
            <Button
              color="blue"
              onClick={review}
              disabled={!chosen.length}
              nospace
            >
              Baixar
            </Button>
          ) : (
            <>
              <Button
                color="lightgrey"
                onClick={() => setStep('edit')}
                disabled={saving}
                nospace
              >
                Voltar
              </Button>
              <Button color="blue" onClick={settle} disabled={saving} nospace>
                {saving ? 'Baixando...' : 'Confirmar baixa'}
              </Button>
            </>
          )}
        </ActionButtons>
      </ActionBar>
    </SettlementContainer>
  );
};

// Contagem que o Historico usa no badge e para habilitar a aba: titulos em
// aberto (para baixar) e baixados (para estornar).
export const countSettlementTitles = (titulos) => ({
  open: (titulos || []).filter((titulo) => titulo?.status !== 'baixado').length,
  settled: (titulos || []).filter((titulo) => titulo?.status === 'baixado')
    .length,
});

// A aba Baixa do Historico: a baixa manual dos titulos em aberto (flag
// `baixa_manual`) e o estorno dos baixados (flag `estorna_baixa`). Quem tem as
// duas escolhe no seletor Em aberto | Baixados; quem tem uma so ve a dela.
//
// Os titulos sao relidos de `GET /titulos` ao abrir e depois de cada baixa ou
// estorno: o `row.titulos` e a foto da listagem, e o estorno devolve a parcela
// para a lista de abertos com mora recalculada, coisa que a foto nao tem.
// `onCounts` informa as contagens ao Historico; `onChanged` recarrega a linha
// do tempo e a listagem; `onClosed` roda quando a baixa encerrou a cobranca.
export const SettlementTab = ({
  row,
  canSettle,
  canReverse,
  onChanged,
  onClosed,
  onCounts,
}) => {
  const [titulos, setTitulos] = useState(row?.titulos || []);
  const [mode, setMode] = useState(canSettle ? 'open' : 'settled');
  // Muda a cada releitura, para a baixa recomecar do zero com a lista nova.
  const [version, setVersion] = useState(0);

  const refresh = useCallback(async () => {
    if (!row?.id) return;
    const result = await ReadByCobranca(row.id);
    if (!result || result?.error) return;
    setTitulos(normalizeResponse(result));
    setVersion((current) => current + 1);
  }, [row?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const counts = useMemo(() => countSettlementTitles(titulos), [titulos]);

  useEffect(() => {
    onCounts?.(counts);
  }, [counts, onCounts]);

  const changed = () => {
    refresh();
    onChanged?.();
  };

  return (
    <SettlementContainer>
      {canSettle && canReverse ? (
        <ModeBar>
          {[
            { id: 'open', label: `Em aberto (${counts.open})` },
            { id: 'settled', label: `Baixados (${counts.settled})` },
          ].map((option) => (
            <Button
              key={option.id}
              color={mode === option.id ? 'blue' : 'lightgrey'}
              small
              nospace
              width="auto"
              onClick={() => setMode(option.id)}
            >
              {option.label}
            </Button>
          ))}
        </ModeBar>
      ) : null}
      {mode === 'open' && canSettle ? (
        <ManualSettlement
          key={version}
          row={{ ...row, titulos }}
          onSettled={changed}
          onClosed={onClosed}
        />
      ) : null}
      {mode === 'settled' && canReverse ? (
        <SettlementReversal
          key={version}
          cobrancaId={row?.id}
          cobrancaStatus={row?.status}
          titulos={titulos}
          onReversed={changed}
        />
      ) : null}
    </SettlementContainer>
  );
};
